import { AtButton, AtButtonProps, NavArrowRight } from '../../atoms'
import { AtButtonDimensions, AtButtonTypes } from '../../types'
import { cn } from '../../utils/cn'
import { Media, OrMedia } from '../or-media'

type OrContentBlockCOrientation = 'normal' | 'reverse'
interface OrContentBlockCTopHalfProps {
  title: string
  mainText: string
  subTitle: string
  body: string
  signature: string
  media?: Media | null
  orientation?: OrContentBlockCOrientation
}

interface OrContentBlockCBottomHalfProps {
  mainTextTwo: string
  titleTwo: string
  bodyTwo: string
  buttonText: string
  mediaTwo?: Media | null
  button?: Pick<AtButtonProps, 'openUrl' | 'target'>
  onClick?: () => void
  orientation?: OrContentBlockCOrientation
  onTrackClick?: () => void
}

type OrContentBlockCPropsVariant = 'full' | 'top-half' | 'bottom-half'
export interface OrContentBlockCProps extends OrContentBlockCTopHalfProps, OrContentBlockCBottomHalfProps {
  dataTestId?: string
  variant?: OrContentBlockCPropsVariant
  onTrackClick?: () => void
}

export const OrContentBlockC = ({
  mainText,
  title,
  subTitle,
  body,
  signature,
  media,
  mainTextTwo,
  titleTwo,
  bodyTwo,
  buttonText,
  mediaTwo: imageTwoProp,
  variant = 'full',
  orientation = 'normal',
  button,
  onClick,
  onTrackClick,
  dataTestId = 'or-content-block-c',
}: OrContentBlockCProps) => {
  const showTopHalf = Boolean(variant === 'top-half' || variant === 'full')
  const showBottomHalf = Boolean(variant === 'bottom-half' || variant === 'full')

  const imageTwo = variant === 'bottom-half' && !imageTwoProp?.src ? media : imageTwoProp

  return (
    <section data-testid={dataTestId} className="mx-auto lg:overflow-x-hidden">
      <div className="lg:hidden">
        {showTopHalf && (
          <OrContentBlockCTopHalfMobile
            mainText={mainText}
            title={title}
            subTitle={subTitle}
            body={body}
            signature={signature}
            media={media}
            orientation={orientation}
          />
        )}
        {showBottomHalf && (
          <OrContentBlockCBottomHalfMobile
            mainTextTwo={mainTextTwo}
            titleTwo={titleTwo}
            bodyTwo={bodyTwo}
            buttonText={buttonText}
            mediaTwo={imageTwo}
            orientation={orientation}
            button={button}
            onClick={onClick}
            onTrackClick={onTrackClick}
          />
        )}
      </div>
      <div className="hidden lg:block">
        {showTopHalf && (
          <OrContentBlockCTopHalf
            mainText={mainText}
            title={title}
            subTitle={subTitle}
            body={body}
            signature={signature}
            media={media}
            orientation={orientation}
          />
        )}
        {showBottomHalf && (
          <OrContentBlockCBottomHalf
            mainTextTwo={mainTextTwo}
            titleTwo={titleTwo}
            bodyTwo={bodyTwo}
            buttonText={buttonText}
            mediaTwo={imageTwo}
            orientation={orientation}
            button={button}
            onClick={onClick}
            onTrackClick={onTrackClick}
          />
        )}
      </div>
    </section>
  )
}

const OrContentBlockCTopHalf = ({
  mainText,
  title,
  subTitle,
  body,
  signature,
  media,
  orientation = 'normal',
}: OrContentBlockCTopHalfProps) => {
  return (
    <div
      className={cn(
        'flex mb-24',
        orientation === 'normal' && 'flex-row',
        orientation === 'reverse' && 'flex-row-reverse'
      )}
    >
      <div className="w-7/12 self-center px-20 lg:pl-[12%]">
        <div className="mb-4">
          <div className="h-1 w-12 bg-secondary-700" />
        </div>
        <div className="mb-6">
          <p className="uppercase text-12 font-bold text-secondary-700">{mainText}</p>
        </div>
        <div className="">
          <h2 className="text-neutrals-900 text-48 font-heading">{title}</h2>
        </div>
        <div className="mb-4">
          <h3 className="text-neutrals-900 text-24 font-heading">{subTitle}</h3>
        </div>
        <div className="mb-4 pr-16">
          <p className="text-neutrals-700 text-16">{body}</p>
        </div>
        <div className="pr-12">
          <p className="font-signature text-[4.5rem] -rotate-6">{signature}</p>
        </div>
      </div>
      <div className="w-5/12">
        <div className="relative mb-8">
          <div
            className={cn(
              orientation === 'normal' && 'pr-[6rem] xl:pr-[14rem]',
              orientation === 'reverse' && 'pl-[6rem] xl:pl-[14rem]'
            )}
          >
            {media && (
              <OrMedia
                {...media}
                className={`w-full h-full object-cover min-w-[13.5rem] ${
                  media.type !== 'image' ? `aspect-${orientation === 'normal' ? '[4/3]' : '[3/4]'}` : ''
                }`}
              />
            )}
          </div>
          <div
            className={cn(
              'absolute w-full h-full rounded-3 bg-neutrals-100 top-[20%] -z-10',
              orientation === 'normal' && 'left-[10%]',
              orientation === 'reverse' && 'right-[10%]'
            )}
          ></div>
        </div>
      </div>
    </div>
  )
}

const OrContentBlockCBottomHalf = ({
  mainTextTwo,
  titleTwo,
  bodyTwo,
  buttonText,
  mediaTwo,
  orientation = 'normal',
  button,
  onClick,
  onTrackClick,
}: OrContentBlockCBottomHalfProps) => {
  return (
    <div
      className={cn('flex', orientation === 'normal' && 'flex-row', orientation === 'reverse' && 'flex-row-reverse')}
    >
      <div className="w-6/12 mt-8">
        <div className="relative">
          <div className="">{mediaTwo && <OrMedia {...mediaTwo} className="w-full h-full object-cover" />}</div>
          <div className="absolute w-11/12 h-full rounded-3 bg-neutrals-100 bottom-[20%] -z-10"></div>
        </div>
      </div>
      <div className="w-6/12 mt-8 self-center">
        <div className="px-30">
          <div className="mb-4">
            <div className="h-1 w-12 bg-secondary-700" />
          </div>
          <div className="mb-6">
            <p className="uppercase text-12 font-bold text-secondary-700">{mainTextTwo}</p>
          </div>
          <div className="mb-4">
            <h2 className="text-neutrals-900 text-48 font-heading">{titleTwo}</h2>
          </div>
          <div className="mb-8">
            <p className="text-neutrals-700 text-16">{bodyTwo}</p>
          </div>
          {button ? (
            <div>
              <AtButton
                title={buttonText}
                type={AtButtonTypes.SECONDARY}
                size={AtButtonDimensions.LARGE}
                IconRightRef={NavArrowRight}
                className="w-[14rem]"
                align="center"
                openUrl={button?.openUrl}
                target={button?.target}
                onClick={button?.openUrl ? onClick : undefined}
                parentName="ContentBlockC"
                onTrackClick={onTrackClick}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const OrContentBlockCTopHalfMobile = ({
  mainText,
  title,
  subTitle,
  body,
  signature,
  media,
  orientation = 'normal',
}: OrContentBlockCTopHalfProps) => {
  return (
    <div>
      <div className="px-4 sm:px-8 slg:px-12">
        <div className="mb-4">
          <div className="h-1 w-12 bg-secondary-700" />
        </div>
        <div className="mb-6">
          <p className="uppercase text-12 font-bold text-secondary-700">{mainText}</p>
        </div>
      </div>
      <div
        className={cn('flex', orientation === 'normal' && 'flex-col', orientation === 'reverse' && 'flex-col-reverse')}
      >
        <div>
          <div className="mb-14">
            <div className="pl-4 sm:pl-8 slg:pl-12 pr-4 sm:pr-8 slg:pr-12">
              {media && (
                <div className="relative w-full max-w-[20rem] sm:max-w-[22rem]">
                  <OrMedia {...media} className="w-full h-auto object-cover rounded-3" />
                  <div className="absolute w-full h-full rounded-3 bg-neutrals-100 left-[20%] top-[10%] -z-10"></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-8 slg:px-12 mb-20 max-w-[750px]">
          <div className="">
            <h2 className="text-neutrals-900 text-32 font-heading">{title}</h2>
          </div>
          <div className="mb-4">
            <h3 className="text-neutrals-900 text-20 font-heading">{subTitle}</h3>
          </div>
          <div className="mb-4">
            <p className="text-neutrals-700 text-14">{body}</p>
          </div>
          <div className="pr-8">
            <p className="font-signature text-48 -rotate-6">{signature}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const OrContentBlockCBottomHalfMobile = ({
  mainTextTwo,
  titleTwo,
  bodyTwo,
  buttonText,
  mediaTwo,
  orientation = 'normal',
  button,
  onClick,
  onTrackClick,
}: OrContentBlockCBottomHalfProps) => {
  return (
    <div>
      <div className="px-4 sm:px-8 slg:px-12">
        <div className="mb-4">
          <div className="h-1 w-12 bg-secondary-700" />
        </div>
        <div className="mb-6">
          <p className="uppercase text-12 font-bold text-secondary-700">{mainTextTwo}</p>
        </div>
      </div>
      <div
        className={cn('flex', orientation === 'normal' && 'flex-col', orientation === 'reverse' && 'flex-col-reverse')}
      >
        <div className="mb-8">
          <div className="px-4 sm:px-8 slg:pl-12 studio-content-block-c-image">
            <div className="relative w-full max-w-[20rem] sm:max-w-[22rem]">
              {mediaTwo && <OrMedia {...mediaTwo} className="w-full h-auto object-cover" />}
              <div className="absolute w-full h-full rounded-3 bg-neutrals-100 left-[8%] top-[8%] -z-10"></div>
            </div>
          </div>
        </div>
        <div className={cn('px-4 sm:px-8 slg:px-12 max-w-[750px]', orientation === 'reverse' && 'pb-[48px]')}>
          <div className="mb-4">
            <h2 className="text-neutrals-900 text-32 font-heading">{titleTwo}</h2>
          </div>
          <div className="mb-8">
            <p className="text-neutrals-700 text-14">{bodyTwo}</p>
          </div>
          {button ? (
            <div>
              <AtButton
                title={buttonText}
                type={AtButtonTypes.SECONDARY}
                size={AtButtonDimensions.LARGE}
                IconRightRef={NavArrowRight}
                className="w-[14rem]"
                align="center"
                openUrl={button?.openUrl}
                target={button?.target}
                onClick={button?.openUrl ? onClick : undefined}
                onTrackClick={onTrackClick}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
